<template>
	<div :class="['input-wrapper', { error: hasError }]">
		<label :for="name">
			<div class="heading-5">
				{{ `${label} ` }}
				<span>{{ ` ${required ? ' *' : ''}` }}</span>
			</div>
		</label>
		<div class="input-container">
			<input
				:id="name"
				ref="customInput"
				:type="inputType"
				:disabled="disabled"
				:class="size"
				:value="localValue"
				:placeholder="placeholder"
				:name="name"
				:maxlength="maxLength"
				autocomplete="off"
				@input="updateValue"
				@blur="onInputBlur"
				@focus="onInputFocus"
				@keypress="isNumber($event)"
				@keydown="onInputKeyDown($event)"
			/>
			<button
				v-if="showClearBtn"
				:aria-label="$t('clear')"
				class="clear-btn"
				@click="clearInput"
			>
				<span aria-hidden="true">x</span>
			</button>
		</div>
		<div
			v-if="options"
			v-show="options.length && focused"
			class="autocomplete-options"
		>
			<div
				v-for="(option, index) in options"
				:key="index"
				:class="[
					'option',
					'body-text-1',
					{ focused: index == currentFocusedElem }
				]"
				@mousedown="chooseOption(option)"
			>
				{{ option }}
			</div>
		</div>
		<div v-show="hasError" class="error-msg">{{ error }}</div>
	</div>
</template>
<script>
import KeyboardNavigation from '@/mixins/KeyboardNavigation';
import { escapeString } from '@/helpers/utils';
export default {
	mixins: [KeyboardNavigation],
	props: {
		placeholder: {
			type: String,
			default: ''
		},

		value: {
			type: [String, Number],
			default: ''
		},
		inputType: {
			type: String,
			default: 'text'
		},
		size: {
			type: String,
			default: 'large',
			validator: value => {
				return ['large', 'mid', 'small'].indexOf(value) !== -1;
			}
		},
		name: {
			type: String,
			required: true
		},

		label: {
			type: String,
			default: ''
		},

		required: {
			type: Boolean,
			default: false
		},

		autocompleteOptions: {
			type: Array,
			default: null
		},

		maxLength: {
			type: Number,
			default: null,
			required: false
		},
		validationCheck: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		isNumberInput: {
			type: Boolean,
			default: false
		},
		escapeString: {
			type: Boolean,
			default: false
		},
		trimSpaces: {
			type: Boolean,
			default: false
		},
		externalErrors: {
			type: Array,
			default: () => []
		},
		showClearBtn: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			error: null,
			localValue: this.value,
			options: this.autocompleteOptions,
			focused: false
		};
	},
	computed: {
		hasError() {
			return !!this.error || this.externalErrors.length;
		}
	},
	watch: {
		value(newValue) {
			if (this.escapeString) {
				newValue = escapeString(newValue);
			}

			if (this.trimSpaces) {
				newValue = newValue.trim();
			}

			this.localValue = newValue;
		},
		autocompleteOptions(newOptions) {
			this.options = newOptions;
		},
		validationCheck() {
			if (this.$props.validationCheck) {
				this.checkForValue();
			}
		},
		externalErrors(value) {
			this.error = value.join('\n');
		}
	},
	created() {
		document.addEventListener('click', this.documentClick);
	},
	destroyed() {
		document.removeEventListener('click', this.documentClick);
	},
	methods: {
		onInputFocus() {
			this.focused = true;
		},
		onInputKeyDown(e) {
			if (this.autocompleteOptions && this.autocompleteOptions.length) {
				switch (e.keyCode) {
					case this.keyboardKeyEvents.ENTER_KEY_CODE:
						if (this.currentFocusedElem >= 0) {
							this.chooseOption(
								this.autocompleteOptions[this.currentFocusedElem]
							);
						}
						return;

					case this.keyboardKeyEvents.DOWN_ARROW_KEY_CODE:
						e.preventDefault();
						e.stopPropagation();
						this.focusNextItem();
						return;

					case this.keyboardKeyEvents.UP_ARROW_KEY_CODE:
						e.preventDefault();
						e.stopPropagation();
						this.focusPrevItem();
						return;

					case this.keyboardKeyEvents.TAB_KEY_CODE:
						this.onInputBlur();
						return;

					default:
						return;
				}
			}
		},
		documentClick() {
			if (this.autocompleteOptions && this.autocompleteOptions.length) {
				this.options = [];
			}
		},
		chooseOption(option) {
			this.$refs.customInput.focus();
			this.localValue = option;
			this.$emit('optionSelected', option);
			this.currentFocusedElem = -1;
		},
		isNumber($event) {
			if (this.isNumberInput) {
				if (
					$event.charCode === 0 ||
					/^-?\d*$/.test(String.fromCharCode($event.charCode))
				) {
					return true;
				} else {
					$event.preventDefault();
				}
			}
		},
		updateValue() {
			this.error = null;

			let refValue = this.$refs.customInput.value;
			if (this.escapeString) {
				refValue = escapeString(refValue);
			}

			this.$emit('input', refValue);
		},

		checkForValue() {
			if (this.required && !this.$refs.customInput.value) {
				this.error = this.$t('requiredInputError', { field: this.label });
			}
		},
		onInputBlur() {
			this.checkForValue();
			this.currentFocusedElem = -1;
			this.options = [];
			this.focused = false;
			this.$emit('blur');
		},
		clearInput() {
			this.$emit('onClearInput');
			this.showClearBtn = false;
		}
	}
};
</script>
<style lang="scss" scoped>
.input-wrapper {
	position: relative;
	width: 100%;
}
label {
	.heading-5 {
		color: $font-dark;
		margin-bottom: 4px;
		span {
			color: $requiredFieldAsterisk;
		}
	}
}
input {
	background-color: $input-bgcolor;
	border: 1px solid $container-border;
	border-radius: 4px;
	outline: 0;
	color: $input-color;
	font-family: 'RobotoRegular';
	font-size: 15px;
	line-height: 23px;
	letter-spacing: 0.5px;

	@media (min-width: $breakpoint-mid) {
		font-size: 18px;
		line-height: 27px;
	}

	&:not(:disabled) {
		&:focus {
			border-color: $hover-state;
		}
	}

	&:disabled {
		background-color: $disabled-state;
		border: 1px solid $disabled-state;
		&:hover {
			cursor: not-allowed;
		}
	}

	&.large {
		height: 48px;
		width: 100%;
		text-indent: 16px;
	}

	&.mid {
		height: 48px;
		width: 240px;
		text-indent: 16px;
	}

	&.small {
		height: 32px;
		width: 84px;
		text-align: center;
	}
}

.autocomplete-options {
	border: 1px solid $container-border;
	border-top: 0;
	border-radius: 4px;
	position: absolute;
	background-color: #ffffff;
	z-index: 2;
	width: 100%;
	max-height: 200px;
	overflow-y: auto;
	box-shadow: 0 15px 30px 0 rgba(0, 76, 208, 0.24);

	.option {
		min-height: 40px;
		line-height: 40px;
		padding-left: 16px;

		&.focused {
			background-color: $hover-option-bgcolor;
		}

		&:hover {
			cursor: pointer;
			background-color: $hover-option-bgcolor;
		}
	}
}

.error {
	input {
		border-color: $error-state;
	}

	label {
		.heading-5 {
			color: $error-state;
		}
	}
}

.error-msg {
	font-family: 'RobotoRegular';
	padding-top: 8px;
	line-height: 22px;
	color: $error-state;
	font-size: 14px;
	letter-spacing: 0.25px;
	line-height: 21px;
	@media (min-width: $breakpoint-mid) {
		font-size: 16px;
		letter-spacing: 0.5px;
		line-height: 24px;
	}
}

.input-container {
	display: flex;
	flex-direction: row-reverse;
	align-items: center;
}
.clear-btn {
	border: 2px solid $icon-error-color;
	border-radius: 50%;
	color: $icon-error-color;
	font-size: 25px;
	background-color: transparent;
	width: 30px;
	height: 30px;
	cursor: pointer;
	transition: all 0.3s ease;
	position: absolute;
	margin: 5px;
	margin-right: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
	span {
		height: 32px;
	}
}

.clear-btn:hover {
	background-color: $icon-error-color;
	color: $font-light;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

/* Firefox */
input[type='number'] {
	-moz-appearance: textfield;
}
</style>
